import './Input.scss';
import { Field } from '@components/Component/Field/Field.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { TokenAutoCompleteMixin } from '@components/Mixin/TokenAutoCompleteMixin/TokenAutoCompleteMixin.js';
import { isCtrlKey } from '@components/Utility/index.js';
/**
 * Input component
 */
export class InputBase extends Field {
    constructor() {
        super(...arguments);
        this.componentName = 'Input';
        /** icon to display inside the input (ie. password inputs and search inputs) */
        this.icon = '';
        this.suppressChangeEvent = false;
        this.hotkeys = {
            ...this.hotkeys // we are extending the hotkeys from the parent class.
            ,
            'enter': (event) => {
                if (this.inputElement.value !== this.value) {
                    this.suppressChangeEvent = true; // change is going to fire too late, but we dont want to run handleUserChange twice as sometimes that results in 2 ajax calls, such as accountNoModalSelect manual entry when pressing enter.
                    // this was needed so if you submit a modal via enter keypress, the new value is updated in time for your modal submission to use it.
                    this.handleUserChange(event);
                    setTimeout(() => {
                        this.suppressChangeEvent = false;
                    }, 100);
                }
                return false; // allow this event to continue propagating upward for stuff like submitting a modal. (TODO: for inputText we cant allow this)
            }
        };
    }
    get inputMode() {
        return this._inputMode;
    }
    set inputMode(value) {
        this._propertySet('inputMode', value);
        this.renderInputMode();
    }
    /** Placeholder text for the input field */
    get placeholder() { return this._placeholder || ''; }
    set placeholder(value) {
        this._propertySet('placeholder', value);
        this.renderPlaceholder();
    }
    captionTypesSet() {
        super.captionTypesSet();
        this.captionTypes.placeholder = 'placeholder';
    }
    draw() {
        super.draw();
        // anything that extends input will probably want to inherit styles
        this.rootElement.classList.add('s360Input');
        // if it got set before drawing, we need to render it now.
        this.renderPlaceholder();
        this.renderInputMode();
        if (this.iconElement) {
            this.registerEvent('click', this.iconElement, this.handleIconClick.bind(this));
        }
    }
    renderPlaceholder() {
        if (!this.inputElement) {
            return;
        }
        const placeholder = this._placeholder;
        if (!placeholder) {
            this.inputElement.removeAttribute('placeholder');
        }
        else {
            this.inputElement.setAttribute('placeholder', placeholder);
        }
    }
    renderInputMode() {
        if (!this.inputElement) {
            return;
        }
        const inputMode = this.inputMode;
        if (!inputMode) {
            this.inputElement.removeAttribute('inputmode');
        }
        else {
            this.inputElement.setAttribute('inputmode', inputMode);
        }
    }
    handleIconClick() {
        this.trigger('iconClick');
    }
    /**
     * setup an event listener for the user change event.
     */
    setupUserChangeEvent() {
        this.registerEvent('change', this.inputElement, (event) => {
            if (this.suppressChangeEvent) {
                // Suppressed due to enter key
                this.suppressChangeEvent = false;
                return;
            }
            if (this.inputElement.value !== this.value) {
                this.handleUserChange(event);
            }
        });
    }
    handleUserChange(event) {
        const oldValue = this._value;
        const newValue = this.inputElement.value;
        this.userChange(newValue, oldValue, event);
    }
    /**
     * Renders the `value` property for this input in the DOM
     * This function is called automatically once the Input has drawn.
     * @returns {Input} - This input instance for chaining calls
     */
    renderValue() {
        if (this.inputElement) {
            this.inputElement.value = this.displayValueGet();
        }
    }
    handlePaste(event) {
        const pastedData = event.clipboardData?.getData('text') || '';
        // Get the current selection length (to account for replacing highlighted text)
        const selectionLength = this.inputElement.selectionEnd - this.inputElement.selectionStart;
        const newValueLength = this.inputElement.value.length - selectionLength + pastedData.length;
        // If the new value exceeds the field length, prevent the paste
        if (newValueLength > this.fieldLength) {
            this.renderLengthExceeded();
            event.preventDefault();
            event.stopPropagation();
        }
    }
    insertTextAtCursor(text) {
        const input = this.inputElement;
        if (!input)
            return;
        const start = input.selectionStart ?? input.value.length;
        const end = input.selectionEnd ?? input.value.length;
        const before = input.value.slice(0, start);
        const after = input.value.slice(end);
        const next = before + text + after;
        const caret = start + text.length;
        input.value = next;
        input.setSelectionRange(caret, caret);
        this.userChange(next, this.value, undefined, false);
    }
    renderLengthExceeded() {
        this.inputElement.classList.add('max-length-reached');
        if (this.maxLengthTimeout) {
            clearInterval(this.maxLengthTimeout);
        }
        this.maxLengthTimeout = setTimeout(() => {
            this.inputElement.classList.remove('max-length-reached');
            this.maxLengthTimeout = undefined;
        }, 2000);
    }
    validateKeyPress(event) {
        let isValidKeyPress = super.validateKeyPress(event);
        // Only proceed if the key is a character input
        const isPrintableKey = event.key.length === 1 && !isCtrlKey(event) && !event.altKey && !event.metaKey;
        if (isValidKeyPress && isPrintableKey) {
            const currentValue = this.inputValue;
            // validate for inputmode
            let selectionStart, selectionEnd, newValue;
            switch (this.inputMode) {
                case 'numeric':
                    isValidKeyPress = (!currentValue && event.key === '-') || (/^[0-9]$/.test(event.key)); // only allows - as the first key or numeric keypresses
                    break;
                case 'decimal':
                    selectionStart = this.inputElement?.selectionStart ?? 0;
                    selectionEnd = this.inputElement?.selectionEnd ?? 0;
                    newValue = currentValue.substring(0, selectionStart) + event.key + currentValue.substring(selectionEnd);
                    isValidKeyPress = (event.key === '-' && newValue.lastIndexOf('-') === 0) ||
                        (event.key === '.' && newValue.indexOf('.') === newValue.lastIndexOf('.')) || // allow '.' only once
                        (/^[0-9]$/.test(event.key)); // only allows '-' as the first key or numeric keypresses or one '.'
                    break;
                // the default is text which allows all keypresses
            }
            // Check the current selection length (for cases where text is selected and will be replaced)
            const selectionLength = this.inputElement.selectionEnd - this.inputElement.selectionStart;
            const newValueLength = this.inputElement.value.length - selectionLength + 1;
            // If the new value exceeds the field length, prevent the input
            if (newValueLength > this.fieldLength) {
                this.renderLengthExceeded();
                isValidKeyPress = false;
            }
        }
        return isValidKeyPress;
    }
    // handleFocus(event: FocusEvent) {
    // 	super.handleFocus(event);
    //
    // 	this.pasteEventId = this.registerEvent<ClipboardEvent>('paste', this.inputElement!, this.handlePaste.bind(this));
    // }
    //
    // handleBlur(event: FocusEvent) {
    // 	super.handleBlur(event);
    // 	if (this.pasteEventId) {
    // 		this.unRegisterEvent(this.pasteEventId);
    // 		delete this.pasteEventId;
    // 	}
    // }
    /**
     * Determines if input value is highlighted.
     */
    isValueHighlighted() {
        let isHighlighted = false;
        if (this.inputElement && this.inputElement.value && this.inputElement.value !== '') {
            const inputElement = this.inputElement;
            if (typeof inputElement.selectionStart == 'number') {
                isHighlighted = inputElement.selectionStart != inputElement.selectionEnd;
            }
            // not sure what below is for.
            // else if (typeof document.selection != "undefined") {
            // 	inputElement.focus();
            // 	isHighlighted = this.inputElement.value.includes(document.selection.createRange().text);
            // }
        }
        return isHighlighted;
    }
    inputTemplate() {
        this.inputElement = document.createElement('input');
        this.inputElement.classList.add('fieldInput');
        this.fieldInputWrapper?.appendChild(this.inputElement);
        if (this.icon) {
            this.iconTemplate();
        }
    }
    iconTemplate() {
        // Create the icon element
        this.iconElement = DIContainer.getInstance('IconManager').getIconElement(this.icon);
        this.iconElement.classList.add('input-icon');
        this.fieldInputWrapper?.appendChild(this.iconElement);
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        if (this.componentName === 'Input') {
            properties.inputMode = ({
                id: 'inputMode',
                value: this.inputMode,
                componentType: 'Select',
                componentProperties: {
                    staticOptionData: [
                        { value: 'text', display: 'Text' },
                        { value: 'numeric', display: 'Numeric' },
                        { value: 'decimal', display: 'Decimal' }
                    ]
                },
                captionKey: 'formbuilder_input_inputmode',
                propertyGroup: 'data',
                propertyIndex: 2.03,
                saveFlag: () => !!this.inputMode
            });
        }
        return properties;
    }
}
InputBase.VERSION = '$Revision: 129338 $';
export class Input extends TokenAutoCompleteMixin(InputBase) {
    draw() {
        super.draw();
        const el = this.inputElement;
        this.tokenHostPort = {
            getText: () => el.value,
            getSelection: () => ({ start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 }),
            replaceRange: (r, txt) => {
                const v = el.value;
                const next = v.slice(0, r.start) + txt + v.slice(r.end);
                const caret = r.start + txt.length;
                el.value = next;
                el.setSelectionRange(caret, caret);
                this.userChange(next, this.value, undefined, false);
            },
            focus: () => el.focus(),
            getCaretClientRect: () => this._measureCaretRect(el),
            element: el
        };
    }
    _measureCaretRect(el) {
        const isTextarea = el instanceof HTMLTextAreaElement;
        const selStart = el.selectionStart ?? 0;
        // Mirror
        const mirror = document.createElement('div');
        const style = window.getComputedStyle(el);
        const props = [
            'boxSizing', 'width', 'height', 'overflow',
            'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
            'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
            'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch',
            'fontSize', 'lineHeight', 'fontFamily',
            'textAlign', 'textTransform', 'textIndent', 'letterSpacing', 'wordSpacing',
            'tabSize', 'whiteSpace'
        ];
        props.forEach(p => mirror.style[p] = style[p]);
        // Normalize whitespace handling
        mirror.style.whiteSpace = isTextarea ? 'pre-wrap' : 'pre';
        mirror.style.wordWrap = 'break-word';
        mirror.style.position = 'fixed'; // anchor to viewport
        const hostRect = el.getBoundingClientRect();
        mirror.style.left = `${hostRect.left}px`;
        mirror.style.top = `${hostRect.top}px`;
        mirror.style.pointerEvents = 'none';
        mirror.style.visibility = 'hidden';
        mirror.style.zIndex = '-1';
        // Build content up to caret + marker
        const beforeText = el.value.slice(0, selStart);
        const before = document.createTextNode(beforeText);
        const marker = document.createElement('span');
        // Zero-width space ensures a measurable rect even at line ends
        marker.textContent = '\u200B';
        // Content after caret to preserve wrapping context slightly
        const after = document.createTextNode(el.value.slice(selStart) || ' ');
        // Create inner container to emulate content box
        const inner = document.createElement('div');
        inner.style.whiteSpace = mirror.style.whiteSpace;
        inner.appendChild(before);
        inner.appendChild(marker);
        inner.appendChild(after);
        mirror.appendChild(inner);
        document.body.appendChild(mirror);
        // Sync scroll to match what the user sees
        mirror.scrollTop = el.scrollTop || 0;
        mirror.scrollLeft = el.scrollLeft || 0;
        const r = marker.getBoundingClientRect();
        const rect = new DOMRect(r.left, r.top, r.width, r.height);
        document.body.removeChild(mirror);
        // Prefer placing the menu below the caret
        return new DOMRect(rect.left, rect.bottom, rect.width, rect.height);
    }
}
